// src/components/CheckoutForm.js
import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { getAuthHeaders } from '../../utils/authUtils';

const CheckoutForm = ({ user, selectedPlan, onPaymentSuccess, onPaymentError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Card styling options
    const cardStyle = {
        style: {
            base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                    color: '#aab7c4',
                },
                padding: '12px',
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    const createSubscription = async (paymentIntentId) => {
        try {
            const subscriptionResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payments/create`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    userId: user.id,
                    planId: selectedPlan.planId,
                    paymentIntentId: paymentIntentId
                })
            });

            if (!subscriptionResponse.ok) {
                const errorData = await subscriptionResponse.json();
                throw new Error(errorData.message || 'Failed to create subscription');
            }

            const subscription = await subscriptionResponse.json();
            return subscription;
        } catch (error) {
            console.error('Subscription creation error:', error);
            throw error;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        if (!stripe || !elements) {
            setLoading(false);
            return;
        }

        if (!selectedPlan) {
            setError('Please select a subscription plan');
            setLoading(false);
            return;
        }

        const cardElement = elements.getElement(CardElement);

        try {
            // Validate plan data
            if (!selectedPlan.price || isNaN(selectedPlan.price) || selectedPlan.price <= 0) {
                throw new Error('Invalid plan price. Please refresh and try again.');
            }

            console.log('Selected plan data:', selectedPlan); // Debug log

            // Step 1: Create payment intent on backend
            const paymentIntentResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payments/create-payment-intent`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    amount: Math.round(selectedPlan.price * 100), // Convert to cents and ensure integer
                    currency: selectedPlan.currency || 'usd',
                    description: `${selectedPlan.planName || 'Premium'} Subscription`,
                    customerEmail: user?.email,
                    metadata: {
                        userId: user?.id?.toString() || '',
                        username: user?.username || '',
                        planId: selectedPlan.planId?.toString() || '',
                        planName: selectedPlan.planName || '',
                        subscriptionType: 'premium'
                    }
                })
            });

            if (!paymentIntentResponse.ok) {
                const errorData = await paymentIntentResponse.json();
                throw new Error(errorData.message || 'Failed to create payment intent');
            }

            const { clientSecret, paymentIntentId } = await paymentIntentResponse.json();

            // Step 2: Confirm payment on frontend
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: user?.username || 'Customer',
                        email: user?.email,
                    },
                }
            });

            if (result.error) {
                throw new Error(result.error.message);
            }

            // Step 3: Payment successful, now create subscription
            console.log('Payment succeeded!', result.paymentIntent);
            
            const subscription = await createSubscription(result.paymentIntent.id);
            console.log('Subscription created successfully!', subscription);
            
            onPaymentSuccess(subscription);

        } catch (err) {
            console.error('Payment/Subscription error:', err);
            const errorMessage = err.message || 'An unexpected error occurred';
            setError(errorMessage);
            onPaymentError(err);
        }

        setLoading(false);
    };

    return (
        <Form onSubmit={handleSubmit}>
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}
            
            <Form.Group className="mb-3">
                <Form.Label>Card Information</Form.Label>
                <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <CardElement options={cardStyle} />
                </div>
                <Form.Text className="text-muted">
                    Your payment information is secure and encrypted.
                </Form.Text>
            </Form.Group>

            {selectedPlan && (
                <div className="mb-3 p-3 bg-light rounded">
                    <h6 className="mb-2">Subscription Details</h6>
                    <div><strong>{selectedPlan.planName || 'Premium Plan'}</strong></div>
                    <div className="text-success">${selectedPlan.price || 'N/A'}/month</div>
                    {selectedPlan.trialPeriodDays && selectedPlan.trialPeriodDays > 0 && (
                        <small className="text-muted">
                            Includes {selectedPlan.trialPeriodDays} days free trial
                        </small>
                    )}
                </div>
            )}

            <div className="d-grid">
                <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    disabled={!stripe || loading || !selectedPlan}
                >
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Processing payment...
                        </>
                    ) : (
                        selectedPlan ? 
                            `Subscribe for $${selectedPlan.price}/month` : 
                            'Select a plan first'
                    )}
                </Button>
            </div>

            <div className="text-center mt-3">
                <small className="text-muted">
                    🔒 Secured by Stripe • Cancel anytime
                </small>
            </div>
        </Form>
    );
};

export default CheckoutForm;